import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
	argbFromHex,
	hexFromArgb,
	themeFromSourceColor,
	type TonalPalette,
} from '@material/material-color-utilities';

/**
 * Kunci cache CSS var terkomputasi di localStorage. Skrip inline di index.html
 * membacanya SEBELUM Angular bootstrap → terapkan tema tenant tanpa kedip (FOUC).
 * Ubah di sini WAJIB sinkron dengan skrip inline di src/index.html.
 */
export const THEME_CACHE_KEY = 'saas_travel_theme_vars';

/** Stop ramp & persentase mix dari warna dasar (500). + = ke putih, − = ke hitam.
 *  IDENTIK dengan ThemeService landing agar warna brand/gold konsisten antar-app. */
const RAMP: { stop: number; mix: number }[] = [
	{ stop: 50, mix: 0.95 },
	{ stop: 100, mix: 0.88 },
	{ stop: 200, mix: 0.74 },
	{ stop: 300, mix: 0.56 },
	{ stop: 400, mix: 0.3 },
	{ stop: 500, mix: 0 },
	{ stop: 600, mix: -0.12 },
	{ stop: 700, mix: -0.26 },
	{ stop: 800, mix: -0.4 },
	{ stop: 900, mix: -0.52 },
	{ stop: 950, mix: -0.68 },
];

const RAMP_STOPS = RAMP.map((r) => r.stop);

/** Peran M3 yang di-override dari warna tenant. Neutral/surface/error/outline
 *  sengaja DIBIARKAN default Material (permukaan tetap netral/putih). */
const MAT_ROLES = ['primary', 'secondary', 'tertiary'] as const;

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb | null {
	let h = (hex || '').trim().replace(/^#/, '');
	if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
	if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
	return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Geser channel ke putih (amt>0) atau hitam (amt<0). */
function mixChannel(c: number, amt: number): number {
	return amt >= 0 ? Math.round(c + (255 - c) * amt) : Math.round(c * (1 + amt));
}

function rgbToHex([r, g, b]: Rgb): string {
	const h = (n: number) => n.toString(16).padStart(2, '0');
	return `#${h(r)}${h(g)}${h(b)}`;
}

/** Normalisasi ke `#rrggbb` atau null bila bukan hex valid. */
function normalizeHex(hex: string | null | undefined): string | null {
	const rgb = hexToRgb(hex ?? '');
	return rgb ? rgbToHex(rgb) : null;
}

/** Bangun deklarasi CSS var --color-<prefix>-<stop> untuk seluruh ramp (Tailwind v4). */
function rampVars(prefix: string, hex: string): string {
	const base = hexToRgb(hex);
	if (!base) return '';
	return RAMP.map(({ stop, mix }) => {
		const c: Rgb = [
			mixChannel(base[0], mix),
			mixChannel(base[1], mix),
			mixChannel(base[2], mix),
		];
		// Tailwind v4 dgn prefix `tw` meng-emit token @theme sebagai --tw-color-*, dan
		// utilitas membaca var(--tw-color-*). WAJIB pakai nama itu agar override runtime
		// mengenai utilitas (tw:bg-brand-*) DAN var() mentah di styles.css.
		return `--tw-color-${prefix}-${stop}:${rgbToHex(c)};`;
	}).join('');
}

/** Token M3 satu keluarga peran (primary/secondary/tertiary) dari palet tonal.
 *  Tone mengikuti skema TERANG Material 3. */
function famVars(role: string, pal: TonalPalette): string {
	const t = (tone: number) => hexFromArgb(pal.tone(tone));
	return (
		`--mat-sys-${role}:${t(40)};` +
		`--mat-sys-on-${role}:${t(100)};` +
		`--mat-sys-${role}-container:${t(90)};` +
		`--mat-sys-on-${role}-container:${t(10)};` +
		`--mat-sys-${role}-fixed:${t(90)};` +
		`--mat-sys-${role}-fixed-dim:${t(80)};` +
		`--mat-sys-on-${role}-fixed:${t(10)};` +
		`--mat-sys-on-${role}-fixed-variant:${t(30)};`
	);
}

/** Override token sistem Material (--mat-sys-*) dari warna primary (+ accent → tertiary). */
function matSysVars(primaryHex: string, accentHex: string | null): string {
	const theme = themeFromSourceColor(argbFromHex(primaryHex));
	const primaryPal = theme.palettes.primary;
	const secondaryPal = theme.palettes.secondary;
	// Accent menyetir keluarga tertiary; tanpa accent, pakai tertiary turunan primary.
	const tertiaryPal = accentHex
		? themeFromSourceColor(argbFromHex(accentHex)).palettes.primary
		: theme.palettes.tertiary;

	return (
		famVars('primary', primaryPal) +
		famVars('secondary', secondaryPal) +
		famVars('tertiary', tertiaryPal) +
		`--mat-sys-surface-tint:${hexFromArgb(primaryPal.tone(40))};` +
		`--mat-sys-inverse-primary:${hexFromArgb(primaryPal.tone(80))};`
	);
}

/** Daftar LENGKAP nama var tema (untuk reset penuh saat clear, apa pun yang menuliskannya). */
function allThemeVarNames(): string[] {
	const names: string[] = [];
	for (const p of ['brand', 'gold']) {
		for (const s of RAMP_STOPS) names.push(`--tw-color-${p}-${s}`);
	}
	for (const r of MAT_ROLES) {
		names.push(
			`--mat-sys-${r}`,
			`--mat-sys-on-${r}`,
			`--mat-sys-${r}-container`,
			`--mat-sys-on-${r}-container`,
			`--mat-sys-${r}-fixed`,
			`--mat-sys-${r}-fixed-dim`,
			`--mat-sys-on-${r}-fixed`,
			`--mat-sys-on-${r}-fixed-variant`,
		);
	}
	names.push('--mat-sys-surface-tint', '--mat-sys-inverse-primary');
	return names;
}

/**
 * Tema warna dinamis per-tenant untuk ADMIN. Dari 1 hex primary & 1 hex accent:
 *  - ramp brand/gold 50–950 → CSS var Tailwind v4 (--color-brand & --color-gold), dan
 *  - palet Material 3 → token --mat-sys-* (primary/secondary/tertiary + fixed).
 * Diterapkan sebagai inline style pada <html> (prioritas tertinggi, menang atas
 * @theme :root maupun mat.theme html{}) + disimpan ke localStorage untuk anti-FOUC.
 *
 * Berbeda dari landing: admin baru tahu tenant setelah login (token localStorage),
 * jadi tema diterapkan client-side pasca-auth; kedip dicegah oleh skrip inline
 * index.html yang me-restore cache sebelum bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
	private readonly doc = inject(DOCUMENT);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly isBrowser = isPlatformBrowser(this.platformId);

	/** Rakit seluruh string var tema. Kosong bila primary bukan hex valid. */
	computeVars(primary: string | null | undefined, accent: string | null | undefined): string {
		const p = normalizeHex(primary);
		if (!p) return '';
		const a = normalizeHex(accent);
		return `${rampVars('brand', p)}${a ? rampVars('gold', a) : ''}${matSysVars(p, a)}`;
	}

	/** Terapkan tema tenant. Primary invalid/kosong → reset ke default (clear). */
	apply(primary: string | null | undefined, accent: string | null | undefined): void {
		const vars = this.computeVars(primary, accent);
		if (!vars) {
			this.clear();
			return;
		}
		this.writeVars(vars);
		if (this.isBrowser) {
			try {
				localStorage.setItem(THEME_CACHE_KEY, vars);
			} catch {
				/* storage penuh/diblokir — abaikan, tema tetap diterapkan runtime */
			}
		}
	}

	/** Kembalikan ke tema default (hapus semua override inline + cache). */
	clear(): void {
		const root = this.doc.documentElement as HTMLElement;
		for (const name of allThemeVarNames()) root.style.removeProperty(name);
		if (this.isBrowser) {
			try {
				localStorage.removeItem(THEME_CACHE_KEY);
			} catch {
				/* abaikan */
			}
		}
	}

	private writeVars(vars: string): void {
		const root = this.doc.documentElement as HTMLElement;
		for (const decl of vars.split(';')) {
			const i = decl.indexOf(':');
			if (i < 0) continue;
			const name = decl.slice(0, i).trim();
			if (!name) continue;
			root.style.setProperty(name, decl.slice(i + 1).trim());
		}
	}
}
